/**
 * 账号安全页面
 * 使用 shadcn + 黑白主题重构
 */

import { useState, useEffect } from "react";
import { useTranslation } from "react-i18next";
import { useOperationRegion } from "@/hooks/useOperationRegion";
import {
  shouldShowSecurityItem,
  isReadOnlyItem,
} from "@/utils/securityRegionUtils";
// Google Identity Services 类型已在 GoogleOAuthButton.tsx 中声明
import {
  Lock,
  Smartphone,
  Mail,
  AlertTriangle,
  CheckCircle2,
  XCircle,
  Loader2,
  Eye,
  EyeOff,
  ChevronRight,
  ChevronDown,
  Brain,
} from "lucide-react";
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Badge } from "@/components/ui/badge";
import { Separator } from "@/components/ui/separator";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import { toast } from "sonner";
import { Skeleton } from "@/components/ui/skeleton";
import { Switch } from "@/components/ui/switch";
import SubsectionHeader from "@/components/shared/SubsectionHeader";
import { userApiClient, ApiException } from "@/lib/userApiClient";

declare global {
  interface Window {
    google?: {
      accounts: {
        id: {
          initialize: (config: any) => void;
          prompt: (notification?: any) => void;
          renderButton: (parent: HTMLElement, options: any) => void;
          disableAutoSelect: () => void;
          storeCredential: (credential: any, callback?: any) => void;
          cancel: () => void;
          revoke: (
            hint: string,
            callback?: (done: { error: string }) => void,
          ) => void;
        };
      };
    };
  }
}

interface SecurityData {
  phone: string | null;
  phone_verified: boolean;
  email: string | null;
  email_verified: boolean;
  pending_email: string | null;
  wechat_linked: boolean;
  wechat_nickname: string | null;
  google_linked: boolean;
  google_email: string | null;
  apple_linked: boolean;
  password_set: boolean;
  ai_training_opt_out: boolean;
}

// 安全项组件
interface SecurityItemProps {
  icon: React.ReactNode;
  title: string;
  description: string;
  status: "verified" | "unverified" | "pending";
  statusText?: string;
  actionText: string;
  onAction: () => void;
  pendingText?: string;
}

function SecurityItem({
  icon,
  title,
  description,
  status,
  statusText,
  actionText,
  onAction,
  pendingText,
  verifiedText,
  unverifiedText,
  pendingLabel,
  readOnly = false,
  readOnlyLabel,
}: SecurityItemProps & {
  verifiedText?: string;
  unverifiedText?: string;
  pendingLabel?: string;
  readOnly?: boolean;
  readOnlyLabel?: string;
}) {
  return (
    <div className="flex items-center justify-between py-4">
      <div className="flex items-center gap-4">
        <div className="flex h-10 w-10 items-center justify-center rounded-full bg-muted">
          {icon}
        </div>
        <div className="space-y-1">
          <div className="flex items-center gap-2">
            <span className="font-medium">{title}</span>
            {status === "verified" && (
              <Badge variant="outline" className="border-foreground/20">
                <CheckCircle2 className="mr-1 h-3 w-3" />
                {verifiedText || "已验证"}
              </Badge>
            )}
            {status === "unverified" && (
              <Badge variant="secondary">{unverifiedText || "未设置"}</Badge>
            )}
            {readOnly && (
              <Badge variant="secondary" className="text-muted-foreground">
                {readOnlyLabel || "只读"}
              </Badge>
            )}
          </div>
          <p className="text-sm text-muted-foreground">
            {statusText || description}
          </p>
          {pendingText && (
            <p className="text-xs text-muted-foreground">
              {pendingLabel || "待确认"}: {pendingText}
            </p>
          )}
        </div>
      </div>
      {!readOnly && (
        <Button variant="ghost" size="sm" onClick={onAction} className="gap-1">
          {actionText}
          <ChevronRight className="h-4 w-4" />
        </Button>
      )}
    </div>
  );
}

export default function Security() {
  const { t } = useTranslation("security");
  const [securityData, setSecurityData] = useState<SecurityData | null>(null);
  const [isLoading, setIsLoading] = useState(true);

  // 运营区域 hook
  const {
    isDomestic,
    loading: regionLoading,
    error: regionError,
  } = useOperationRegion();

  // 合并加载状态：region 和 security data 都加载完成才算完成
  const isPageLoading = isLoading || regionLoading;

  // 对话框状态
  const [passwordDialogOpen, setPasswordDialogOpen] = useState(false);
  const [phoneDialogOpen, setPhoneDialogOpen] = useState(false);
  const [emailDialogOpen, setEmailDialogOpen] = useState(false);
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [unlinkGoogleDialogOpen, setUnlinkGoogleDialogOpen] = useState(false);

  // Google SDK 状态
  const [googleSdkLoaded, setGoogleSdkLoaded] = useState(false);
  const [isLinkingGoogle, setIsLinkingGoogle] = useState(false);

  // 表单状态
  const [currentPassword, setCurrentPassword] = useState("");
  const [newPassword, setNewPassword] = useState("");
  const [confirmPassword, setConfirmPassword] = useState("");
  const [newPhone, setNewPhone] = useState("");
  const [verificationCode, setVerificationCode] = useState("");
  const [newEmail, setNewEmail] = useState("");
  const [deletePassword, setDeletePassword] = useState("");

  // 密码可见性
  const [showCurrentPassword, setShowCurrentPassword] = useState(false);
  const [showNewPassword, setShowNewPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);

  // 提交状态
  const [isSubmitting, setIsSubmitting] = useState(false);

  // 危险操作区域展开状态
  const [dangerZoneExpanded, setDangerZoneExpanded] = useState(false);
  // 数据与隐私区域展开状态
  const [privacyExpanded, setPrivacyExpanded] = useState(false);

  // 验证码相关
  const [isSendingCode, setIsSendingCode] = useState(false);
  const [countdown, setCountdown] = useState(0);

  // 加载安全数据
  const loadSecurityData = async () => {
    setIsLoading(true);
    try {
      const data = await userApiClient.get<SecurityData>("/security");
      setSecurityData(data);
    } catch (error) {
      console.error("加载安全信息失败:", error);
      toast.error(t("loadFailed"));
    } finally {
      setIsLoading(false);
    }
  };

  // 修改密码
  const handleUpdatePassword = async () => {
    if (!newPassword || !confirmPassword) {
      toast.error(t("password.fillComplete"));
      return;
    }
    if (newPassword !== confirmPassword) {
      toast.error(t("password.notMatch"));
      return;
    }
    if (newPassword.length < 6) {
      toast.error(t("password.tooShort"));
      return;
    }
    if (securityData?.password_set && !currentPassword) {
      toast.error(t("password.enterCurrent"));
      return;
    }

    setIsSubmitting(true);
    try {
      const data = await userApiClient.put<{ message: string }>(
        "/security/password",
        {
          current_password: currentPassword,
          new_password: newPassword,
          new_password_confirmation: confirmPassword,
        },
      );

      toast.success(data.message || t("password.updateSuccess"));
      setPasswordDialogOpen(false);
      setCurrentPassword("");
      setNewPassword("");
      setConfirmPassword("");
      loadSecurityData();
    } catch (error) {
      console.error("修改密码失败:", error);
      if (error instanceof ApiException) {
        toast.error(error.message);
      } else {
        toast.error(t("networkError"));
      }
    } finally {
      setIsSubmitting(false);
    }
  };

  // 发送验证码
  const handleSendCode = async () => {
    if (!newPhone) {
      toast.error(t("phone.enterPhone"));
      return;
    }
    if (!/^1[3-9]\d{9}$/.test(newPhone)) {
      toast.error(t("phone.invalidPhone"));
      return;
    }

    setIsSendingCode(true);
    try {
      const data = await userApiClient.post<{ message: string }>(
        "/security/phone/send_code",
        { phone: newPhone },
      );
      toast.success(data.message || t("phone.codeSent"));
      setCountdown(60);
    } catch (error) {
      console.error("发送验证码失败:", error);
      if (error instanceof ApiException) {
        toast.error(error.message);
      } else {
        toast.error(t("networkError"));
      }
    } finally {
      setIsSendingCode(false);
    }
  };

  // 修改手机号
  const handleUpdatePhone = async () => {
    if (!newPhone || !/^1[3-9]\d{9}$/.test(newPhone)) {
      toast.error(t("phone.enterCorrectPhone"));
      return;
    }
    if (!verificationCode) {
      toast.error(t("phone.enterCode"));
      return;
    }

    setIsSubmitting(true);
    try {
      const data = await userApiClient.put<{ message: string }>(
        "/security/phone",
        {
          phone: newPhone,
          verification_code: verificationCode,
        },
      );

      toast.success(data.message || t("phone.updateSuccess"));
      setPhoneDialogOpen(false);
      setNewPhone("");
      setVerificationCode("");
      setCountdown(0);
      loadSecurityData();
    } catch (error) {
      console.error("修改手机号失败:", error);
      if (error instanceof ApiException) {
        toast.error(error.message);
      } else {
        toast.error(t("networkError"));
      }
    } finally {
      setIsSubmitting(false);
    }
  };

  // 修改邮箱
  const handleUpdateEmail = async () => {
    if (
      !newEmail ||
      !/^[\w+\-.]+@[a-z\d\-]+(\.[a-z\d\-]+)*\.[a-z]+$/i.test(newEmail)
    ) {
      toast.error(t("email.enterCorrectEmail"));
      return;
    }

    setIsSubmitting(true);
    try {
      const data = await userApiClient.put<{ message: string }>(
        "/security/email",
        { email: newEmail },
      );

      toast.success(data.message || t("email.verificationSent"));
      setEmailDialogOpen(false);
      setNewEmail("");
      loadSecurityData();
    } catch (error) {
      console.error("修改邮箱失败:", error);
      if (error instanceof ApiException) {
        toast.error(error.message);
      } else {
        toast.error(t("networkError"));
      }
    } finally {
      setIsSubmitting(false);
    }
  };

  // 注销账号
  const handleDeleteAccount = async () => {
    if (securityData?.password_set && !deletePassword) {
      toast.error(t("dangerZone.enterPasswordConfirm"));
      return;
    }

    setIsSubmitting(true);
    try {
      const data = await userApiClient.request<{ message: string }>(
        "/security/account",
        {
          method: "DELETE",
          body: JSON.stringify({ password: deletePassword }),
        },
      );

      toast.success(data.message || t("dangerZone.deleteSuccess"));
      setTimeout(() => {
        window.location.href = "/users/sign_out";
      }, 2000);
    } catch (error) {
      console.error("注销账号失败:", error);
      if (error instanceof ApiException) {
        toast.error(error.message);
      } else {
        toast.error(t("networkError"));
      }
    } finally {
      setIsSubmitting(false);
    }
  };

  // 处理 Google 凭证回调（绑定）
  const handleGoogleCredential = async (response: { credential: string }) => {
    const idToken = response.credential;
    if (!idToken) {
      toast.error(t("thirdParty.googleAuthFailed"));
      return;
    }

    setIsLinkingGoogle(true);
    try {
      const data = await userApiClient.post<{ message: string }>(
        "/security/google",
        { id_token: idToken },
      );

      toast.success(data.message || t("thirdParty.googleBindSuccess"));
      loadSecurityData();
    } catch (error) {
      console.error("Google 绑定失败:", error);
      if (error instanceof ApiException) {
        toast.error(error.message);
      } else {
        toast.error(t("networkError"));
      }
    } finally {
      setIsLinkingGoogle(false);
    }
  };

  // 触发 Google 绑定
  const handleLinkGoogle = () => {
    if (!googleSdkLoaded || !window.google?.accounts?.id) {
      toast.error(t("thirdParty.googleUnavailable"));
      return;
    }

    try {
      window.google.accounts.id.prompt();
    } catch (error) {
      console.error("Failed to show Google Sign-In:", error);
      toast.error(t("thirdParty.googleOpenFailed"));
    }
  };

  // 解绑 Google 账号
  const handleUnlinkGoogle = async () => {
    setIsSubmitting(true);
    try {
      const data = await userApiClient.request<{ message: string }>(
        "/security/google",
        {
          method: "DELETE",
        },
      );

      toast.success(data.message || t("thirdParty.googleUnbindSuccess"));
      setUnlinkGoogleDialogOpen(false);
      loadSecurityData();
    } catch (error) {
      console.error("解绑 Google 失败:", error);
      if (error instanceof ApiException) {
        toast.error(error.message);
      } else {
        toast.error(t("networkError"));
      }
    } finally {
      setIsSubmitting(false);
    }
  };

  // 更新 AI 训练数据设置
  const handleUpdateAiTraining = async (optOut: boolean) => {
    try {
      const data = await userApiClient.put<{ message: string }>(
        "/security/ai_training",
        { ai_training_opt_out: optOut },
      );

      toast.success(data.message || t("aiTraining.updateSuccess"));
      loadSecurityData();
    } catch (error) {
      console.error("更新 AI 训练设置失败:", error);
      if (error instanceof ApiException) {
        toast.error(error.message);
      } else {
        toast.error(t("networkError"));
      }
    }
  };

  // 倒计时效果
  useEffect(() => {
    if (countdown > 0) {
      const timer = setTimeout(() => setCountdown(countdown - 1), 1000);
      return () => clearTimeout(timer);
    }
  }, [countdown]);

  // Google SDK 初始化
  useEffect(() => {
    const checkGoogleSDK = () => {
      if (window.google?.accounts?.id) {
        setGoogleSdkLoaded(true);
        return true;
      }
      return false;
    };

    if (checkGoogleSDK()) return;

    let attempts = 0;
    const maxAttempts = 10;
    const interval = setInterval(() => {
      attempts++;
      if (checkGoogleSDK() || attempts >= maxAttempts) {
        clearInterval(interval);
      }
    }, 500);

    return () => clearInterval(interval);
  }, []);

  // 初始化 Google Sign-In（用于绑定）
  useEffect(() => {
    if (!googleSdkLoaded || !window.google?.accounts?.id) return;

    const clientId =
      (import.meta as any).env?.VITE_GOOGLE_CLIENT_ID ||
      document.querySelector<HTMLMetaElement>('meta[name="google-client-id"]')
        ?.content ||
      "";

    if (!clientId) return;

    try {
      window.google.accounts.id.initialize({
        client_id: clientId,
        callback: handleGoogleCredential,
        auto_select: false,
        cancel_on_tap_outside: true,
        context: "signin",
        use_fedcm_for_prompt: false,
      });
    } catch (error) {
      console.error("Failed to initialize Google Sign-In:", error);
    }
  }, [googleSdkLoaded]);

  // 页面加载
  useEffect(() => {
    loadSecurityData();
    const urlParams = new URLSearchParams(window.location.search);
    if (urlParams.get("email_confirmed") === "true") {
      toast.success(t("email.confirmed"));
      window.history.replaceState({}, "", "/security");
    }
  }, [t]);

  // 加载状态 - 骨架屏（region 或 security data 任一加载中都显示）
  if (isPageLoading) {
    return (
      <div className="container mx-auto max-w-4xl px-4 py-4">
        {/* 页面标题 - 直接显示 */}
        <SubsectionHeader
          title={t("title")}
          icon="Shield"
          description={t("description")}
          className="mb-8"
        />
        <Card className="mb-6">
          <CardHeader>
            <Skeleton className="h-6 w-24 mb-1" />
            <Skeleton className="h-4 w-48" />
          </CardHeader>
          <CardContent className="space-y-4">
            {[1, 2, 3].map((i) => (
              <div key={i} className="flex items-center justify-between py-4">
                <div className="flex items-center gap-4">
                  <Skeleton className="h-10 w-10 rounded-full" />
                  <div className="space-y-2">
                    <Skeleton className="h-5 w-24" />
                    <Skeleton className="h-4 w-32" />
                  </div>
                </div>
                <Skeleton className="h-8 w-16" />
              </div>
            ))}
          </CardContent>
        </Card>
        <Card className="mb-6">
          <CardHeader>
            <Skeleton className="h-6 w-24 mb-1" />
            <Skeleton className="h-4 w-48" />
          </CardHeader>
          <CardContent className="space-y-4">
            {[1, 2, 3].map((i) => (
              <div key={i} className="flex items-center justify-between py-4">
                <div className="flex items-center gap-4">
                  <Skeleton className="h-10 w-10 rounded-full" />
                  <div className="space-y-2">
                    <Skeleton className="h-5 w-20" />
                    <Skeleton className="h-4 w-28" />
                  </div>
                </div>
                <Skeleton className="h-8 w-16" />
              </div>
            ))}
          </CardContent>
        </Card>
      </div>
    );
  }

  // 加载失败
  if (!securityData) {
    return (
      <div className="container mx-auto max-w-4xl px-4 py-4">
        <Card className="p-12 text-center">
          <XCircle className="mx-auto h-12 w-12 text-muted-foreground" />
          <p className="mt-4 text-muted-foreground">{t("loadFailedRetry")}</p>
        </Card>
      </div>
    );
  }

  return (
    <div className="container mx-auto max-w-4xl px-4 py-4">
      {/* 页面标题 */}
      <SubsectionHeader
        title={t("title")}
        icon="Shield"
        description={t("description")}
        className="mb-8"
      />

      {/* 安全设置卡片 */}
      <Card className="mb-6">
        <CardHeader>
          <CardTitle className="text-lg">
            {t("securitySettings.title")}
          </CardTitle>
          <CardDescription>{t("securitySettings.description")}</CardDescription>
        </CardHeader>
        <CardContent className="space-y-0">
          {/* 登录密码 - 始终显示 */}
          <SecurityItem
            icon={<Lock className="h-5 w-5 text-muted-foreground" />}
            title={t("password.title")}
            description={t("password.description")}
            status={securityData.password_set ? "verified" : "unverified"}
            statusText={
              securityData.password_set
                ? t("password.placeholder")
                : t("password.suggestSet")
            }
            actionText={
              securityData.password_set ? t("actions.modify") : t("actions.set")
            }
            onAction={() => setPasswordDialogOpen(true)}
            verifiedText={t("status.verified")}
            unverifiedText={t("status.unverified")}
          />

          {/* 手机号 - 国内模式显示，或海外模式下有历史绑定时只读显示 */}
          {shouldShowSecurityItem(
            "phone",
            isDomestic,
            !!securityData.phone,
          ) && (
            <>
              <Separator />
              <SecurityItem
                icon={<Smartphone className="h-5 w-5 text-muted-foreground" />}
                title={t("phone.title")}
                description={t("phone.description")}
                status={securityData.phone_verified ? "verified" : "unverified"}
                statusText={securityData.phone || t("phone.unbound")}
                actionText={
                  securityData.phone ? t("actions.modify") : t("actions.bind")
                }
                onAction={() => setPhoneDialogOpen(true)}
                verifiedText={t("status.verified")}
                unverifiedText={t("status.unverified")}
                readOnly={isReadOnlyItem(
                  "phone",
                  isDomestic,
                  !!securityData.phone,
                )}
                readOnlyLabel={t("status.readOnly")}
              />
            </>
          )}

          {/* 邮箱 - 海外模式显示，或国内模式下有历史绑定时只读显示 */}
          {shouldShowSecurityItem(
            "email",
            isDomestic,
            !!securityData.email,
          ) && (
            <>
              <Separator />
              <SecurityItem
                icon={<Mail className="h-5 w-5 text-muted-foreground" />}
                title={t("email.title")}
                description={t("email.description")}
                status={securityData.email_verified ? "verified" : "unverified"}
                statusText={securityData.email || t("email.unbound")}
                actionText={
                  securityData.email ? t("actions.modify") : t("actions.bind")
                }
                onAction={() => setEmailDialogOpen(true)}
                pendingText={securityData.pending_email || undefined}
                verifiedText={t("status.verified")}
                unverifiedText={t("status.unverified")}
                pendingLabel={t("status.pending")}
                readOnly={isReadOnlyItem(
                  "email",
                  isDomestic,
                  !!securityData.email,
                )}
                readOnlyLabel={t("status.readOnly")}
              />
            </>
          )}
        </CardContent>
      </Card>

      {/* 第三方账号 */}
      <Card className="mb-6">
        <CardHeader>
          <CardTitle className="text-lg">{t("thirdParty.title")}</CardTitle>
          <CardDescription>{t("thirdParty.description")}</CardDescription>
        </CardHeader>
        <CardContent className="space-y-0">
          {/* 微信 - 国内模式显示，或海外模式下有历史绑定时只读显示 */}
          {shouldShowSecurityItem(
            "wechat",
            isDomestic,
            securityData.wechat_linked,
          ) && (
            <>
              <div className="flex items-center justify-between py-4">
                <div className="flex items-center gap-4">
                  <div className="flex h-10 w-10 items-center justify-center rounded-full bg-muted">
                    <svg
                      className="h-5 w-5 text-[#07c160]"
                      viewBox="0 0 24 24"
                      fill="currentColor"
                    >
                      <path d="M8.691 2.188C3.891 2.188 0 5.476 0 9.53c0 2.212 1.17 4.203 3.002 5.55a.59.59 0 0 1 .213.665l-.39 1.48c-.019.07-.048.141-.048.213 0 .163.13.295.29.295a.326.326 0 0 0 .167-.054l1.903-1.114a.864.864 0 0 1 .717-.098 10.16 10.16 0 0 0 2.837.403c.276 0 .543-.027.811-.05-.857-2.578.157-4.972 1.932-6.446 1.703-1.415 3.882-1.98 5.853-1.838-.576-3.583-4.196-6.348-8.596-6.348zM5.785 5.991c.642 0 1.162.529 1.162 1.18a1.17 1.17 0 0 1-1.162 1.178A1.17 1.17 0 0 1 4.623 7.17c0-.651.52-1.18 1.162-1.18zm5.813 0c.642 0 1.162.529 1.162 1.18a1.17 1.17 0 0 1-1.162 1.178 1.17 1.17 0 0 1-1.162-1.178c0-.651.52-1.18 1.162-1.18zm5.34 2.867c-1.797-.052-3.746.512-5.28 1.786-1.72 1.428-2.687 3.72-1.78 6.22.942 2.453 3.666 4.229 6.884 4.229.826 0 1.622-.12 2.361-.336a.722.722 0 0 1 .598.082l1.584.926a.272.272 0 0 0 .14.047c.134 0 .24-.111.24-.247 0-.06-.023-.12-.038-.177l-.327-1.233a.582.582 0 0 1-.023-.156.49.49 0 0 1 .201-.398C23.024 18.48 24 16.82 24 14.98c0-3.21-2.931-5.837-6.656-6.088V8.89c-.135-.01-.269-.03-.407-.03zm-2.53 3.274c.535 0 .969.44.969.982a.976.976 0 0 1-.969.983.976.976 0 0 1-.969-.983c0-.542.434-.982.97-.982zm4.844 0c.535 0 .969.44.969.982a.976.976 0 0 1-.969.983.976.976 0 0 1-.969-.983c0-.542.434-.982.969-.982z" />
                    </svg>
                  </div>
                  <div className="space-y-1">
                    <div className="flex items-center gap-2">
                      <span className="font-medium">
                        {t("thirdParty.wechat")}
                      </span>
                      {securityData.wechat_linked ? (
                        <Badge
                          variant="outline"
                          className="border-foreground/20"
                        >
                          {t("status.linked")}
                        </Badge>
                      ) : (
                        <Badge variant="secondary">
                          {t("status.unlinked")}
                        </Badge>
                      )}
                      {isReadOnlyItem(
                        "wechat",
                        isDomestic,
                        securityData.wechat_linked,
                      ) && (
                        <Badge
                          variant="secondary"
                          className="text-muted-foreground"
                        >
                          {t("status.readOnly")}
                        </Badge>
                      )}
                    </div>
                    <p className="text-sm text-muted-foreground">
                      {securityData.wechat_linked
                        ? securityData.wechat_nickname || t("status.linked")
                        : t("thirdParty.wechatUnbound")}
                    </p>
                  </div>
                </div>
                {/* 只读模式下不显示操作按钮 */}
                {!isReadOnlyItem(
                  "wechat",
                  isDomestic,
                  securityData.wechat_linked,
                ) &&
                  !securityData.wechat_linked && (
                    <Button
                      variant="ghost"
                      size="sm"
                      className="gap-1"
                      onClick={() => {
                        // 跳转到微信绑定页面，传递 mode=bind 参数
                        window.location.href = "/users/auth/wechat?mode=bind";
                      }}
                    >
                      {t("actions.bind")}
                      <ChevronRight className="h-4 w-4" />
                    </Button>
                  )}
              </div>
              <Separator />
            </>
          )}

          {/* Google - 海外模式显示，或国内模式下有历史绑定时只读显示 */}
          {shouldShowSecurityItem(
            "google",
            isDomestic,
            securityData.google_linked,
          ) && (
            <>
              <div className="flex items-center justify-between py-4">
                <div className="flex items-center gap-4">
                  <div className="flex h-10 w-10 items-center justify-center rounded-full bg-muted">
                    <svg className="h-5 w-5" viewBox="0 0 24 24">
                      <path
                        fill="#4285F4"
                        d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z"
                      />
                      <path
                        fill="#34A853"
                        d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z"
                      />
                      <path
                        fill="#FBBC05"
                        d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z"
                      />
                      <path
                        fill="#EA4335"
                        d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z"
                      />
                    </svg>
                  </div>
                  <div className="space-y-1">
                    <div className="flex items-center gap-2">
                      <span className="font-medium">
                        {t("thirdParty.google")}
                      </span>
                      {securityData.google_linked ? (
                        <Badge
                          variant="outline"
                          className="border-foreground/20"
                        >
                          {t("status.linked")}
                        </Badge>
                      ) : (
                        <Badge variant="secondary">
                          {t("status.unlinked")}
                        </Badge>
                      )}
                      {isReadOnlyItem(
                        "google",
                        isDomestic,
                        securityData.google_linked,
                      ) && (
                        <Badge
                          variant="secondary"
                          className="text-muted-foreground"
                        >
                          {t("status.readOnly")}
                        </Badge>
                      )}
                    </div>
                    <p className="text-sm text-muted-foreground">
                      {securityData.google_linked
                        ? securityData.google_email ||
                          t("thirdParty.googleBound")
                        : t("thirdParty.googleUnbound")}
                    </p>
                  </div>
                </div>
                {/* 只读模式下不显示操作按钮 */}
                {!isReadOnlyItem(
                  "google",
                  isDomestic,
                  securityData.google_linked,
                ) &&
                  (securityData.google_linked ? (
                    <Button
                      variant="ghost"
                      size="sm"
                      className="gap-1 text-destructive hover:text-destructive"
                      onClick={() => setUnlinkGoogleDialogOpen(true)}
                    >
                      {t("actions.unbind")}
                      <ChevronRight className="h-4 w-4" />
                    </Button>
                  ) : (
                    <Button
                      variant="ghost"
                      size="sm"
                      className="gap-1"
                      onClick={handleLinkGoogle}
                      disabled={isLinkingGoogle || !googleSdkLoaded}
                    >
                      {isLinkingGoogle ? (
                        <Loader2 className="h-4 w-4 animate-spin" />
                      ) : (
                        <>
                          {t("actions.bind")}
                          <ChevronRight className="h-4 w-4" />
                        </>
                      )}
                    </Button>
                  ))}
              </div>
              <Separator />
            </>
          )}

          {/* Apple - 海外模式显示，或国内模式下有历史绑定时只读显示 */}
          {shouldShowSecurityItem(
            "apple",
            isDomestic,
            securityData.apple_linked,
          ) && (
            <div className="flex items-center justify-between py-4">
              <div className="flex items-center gap-4">
                <div className="flex h-10 w-10 items-center justify-center rounded-full bg-muted">
                  <svg
                    className="h-5 w-5 text-foreground"
                    viewBox="0 0 24 24"
                    fill="currentColor"
                  >
                    <path d="M17.05 20.28c-.98.95-2.05.8-3.08.35-1.09-.46-2.09-.48-3.24 0-1.44.62-2.2.44-3.06-.35C2.79 15.25 3.51 7.59 9.05 7.31c1.35.07 2.29.74 3.08.8 1.18-.24 2.31-.93 3.57-.84 1.51.12 2.65.72 3.4 1.8-3.12 1.87-2.38 5.98.48 7.13-.57 1.5-1.31 2.99-2.54 4.09l.01-.01zM12.03 7.25c-.15-2.23 1.66-4.07 3.74-4.25.29 2.58-2.34 4.5-3.74 4.25z" />
                  </svg>
                </div>
                <div className="space-y-1">
                  <div className="flex items-center gap-2">
                    <span className="font-medium">{t("thirdParty.apple")}</span>
                    {securityData.apple_linked ? (
                      <Badge variant="outline" className="border-foreground/20">
                        {t("status.linked")}
                      </Badge>
                    ) : (
                      <Badge variant="secondary">{t("status.unlinked")}</Badge>
                    )}
                    {isReadOnlyItem(
                      "apple",
                      isDomestic,
                      securityData.apple_linked,
                    ) && (
                      <Badge
                        variant="secondary"
                        className="text-muted-foreground"
                      >
                        {t("status.readOnly")}
                      </Badge>
                    )}
                  </div>
                  <p className="text-sm text-muted-foreground">
                    {t("thirdParty.appleUnbound")}
                  </p>
                </div>
              </div>
              {/* 只读模式下不显示操作按钮 */}
              {!isReadOnlyItem(
                "apple",
                isDomestic,
                securityData.apple_linked,
              ) && (
                <Button variant="ghost" size="sm" className="gap-1" disabled>
                  {t("actions.comingSoon")}
                </Button>
              )}
            </div>
          )}
        </CardContent>
      </Card>

      {/* 数据与隐私 */}
      <Card className="mb-6">
        <CardHeader
          className="cursor-pointer select-none"
          onClick={() => setPrivacyExpanded(!privacyExpanded)}
        >
          <div className="flex items-center justify-between">
            <CardTitle className="flex items-center gap-2 text-lg">
              <Brain className="h-5 w-5" />
              {t("aiTraining.title")}
            </CardTitle>
            <ChevronDown
              className={`h-5 w-5 text-muted-foreground transition-transform duration-200 ${
                privacyExpanded ? "rotate-180" : ""
              }`}
            />
          </div>
          <CardDescription>{t("aiTraining.description")}</CardDescription>
        </CardHeader>
        {privacyExpanded && (
          <CardContent className="pt-0">
            <Separator className="mb-4" />
            <div className="flex items-center justify-between py-2">
              <div className="space-y-1">
                <p className="font-medium">{t("aiTraining.optOutTitle")}</p>
                <p className="text-sm text-muted-foreground">
                  {t("aiTraining.optOutDescription")}
                </p>
                <p className="text-xs text-muted-foreground">
                  {t("aiTraining.optOutHint")}
                </p>
              </div>
              <Switch
                checked={!securityData.ai_training_opt_out}
                onCheckedChange={(checked) => handleUpdateAiTraining(!checked)}
              />
            </div>
          </CardContent>
        )}
      </Card>

      {/* 危险操作 */}
      <Card className="border-destructive/20">
        <CardHeader
          className="cursor-pointer select-none"
          onClick={() => setDangerZoneExpanded(!dangerZoneExpanded)}
        >
          <div className="flex items-center justify-between">
            <CardTitle className="flex items-center gap-2 text-lg text-destructive">
              <AlertTriangle className="h-5 w-5" />
              {t("dangerZone.title")}
            </CardTitle>
            <ChevronDown
              className={`h-5 w-5 text-muted-foreground transition-transform duration-200 ${
                dangerZoneExpanded ? "rotate-180" : ""
              }`}
            />
          </div>
          <CardDescription>{t("dangerZone.description")}</CardDescription>
        </CardHeader>
        {dangerZoneExpanded && (
          <CardContent className="pt-0">
            <Separator className="mb-4" />
            <div className="space-y-3">
              <p className="text-sm text-muted-foreground">
                {t("dangerZone.warning")}
              </p>
              <Button
                variant="destructive"
                onClick={() => setDeleteDialogOpen(true)}
              >
                {t("dangerZone.deleteAccount")}
              </Button>
            </div>
          </CardContent>
        )}
      </Card>

      {/* 修改密码对话框 */}
      <Dialog open={passwordDialogOpen} onOpenChange={setPasswordDialogOpen}>
        <DialogContent className="sm:max-w-md">
          <DialogHeader>
            <DialogTitle>
              {securityData.password_set
                ? t("password.dialogTitle")
                : t("password.dialogTitleSet")}
            </DialogTitle>
            <DialogDescription>
              {securityData.password_set
                ? t("password.dialogDescription")
                : t("password.dialogDescriptionSet")}
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-4 py-4">
            {securityData.password_set && (
              <div className="space-y-2">
                <Label htmlFor="current-password">
                  {t("password.currentPassword")}
                </Label>
                <div className="relative">
                  <Input
                    id="current-password"
                    type={showCurrentPassword ? "text" : "password"}
                    value={currentPassword}
                    onChange={(e) => setCurrentPassword(e.target.value)}
                    placeholder={t("password.currentPasswordPlaceholder")}
                    className="pr-10"
                  />
                  <button
                    type="button"
                    onClick={() => setShowCurrentPassword(!showCurrentPassword)}
                    className="absolute right-3 top-1/2 -translate-y-1/2 text-muted-foreground hover:text-foreground"
                  >
                    {showCurrentPassword ? (
                      <EyeOff className="h-4 w-4" />
                    ) : (
                      <Eye className="h-4 w-4" />
                    )}
                  </button>
                </div>
              </div>
            )}
            <div className="space-y-2">
              <Label htmlFor="new-password">{t("password.newPassword")}</Label>
              <div className="relative">
                <Input
                  id="new-password"
                  type={showNewPassword ? "text" : "password"}
                  value={newPassword}
                  onChange={(e) => setNewPassword(e.target.value)}
                  placeholder={t("password.newPasswordPlaceholder")}
                  className="pr-10"
                />
                <button
                  type="button"
                  onClick={() => setShowNewPassword(!showNewPassword)}
                  className="absolute right-3 top-1/2 -translate-y-1/2 text-muted-foreground hover:text-foreground"
                >
                  {showNewPassword ? (
                    <EyeOff className="h-4 w-4" />
                  ) : (
                    <Eye className="h-4 w-4" />
                  )}
                </button>
              </div>
            </div>
            <div className="space-y-2">
              <Label htmlFor="confirm-password">
                {t("password.confirmPassword")}
              </Label>
              <div className="relative">
                <Input
                  id="confirm-password"
                  type={showConfirmPassword ? "text" : "password"}
                  value={confirmPassword}
                  onChange={(e) => setConfirmPassword(e.target.value)}
                  placeholder={t("password.confirmPasswordPlaceholder")}
                  className="pr-10"
                />
                <button
                  type="button"
                  onClick={() => setShowConfirmPassword(!showConfirmPassword)}
                  className="absolute right-3 top-1/2 -translate-y-1/2 text-muted-foreground hover:text-foreground"
                >
                  {showConfirmPassword ? (
                    <EyeOff className="h-4 w-4" />
                  ) : (
                    <Eye className="h-4 w-4" />
                  )}
                </button>
              </div>
            </div>
          </div>
          <DialogFooter>
            <Button
              variant="outline"
              onClick={() => setPasswordDialogOpen(false)}
              disabled={isSubmitting}
            >
              {t("actions.cancel")}
            </Button>
            <Button onClick={handleUpdatePassword} disabled={isSubmitting}>
              {isSubmitting && (
                <Loader2 className="mr-2 h-4 w-4 animate-spin" />
              )}
              {t("actions.confirm")}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* 修改手机号对话框 */}
      <Dialog open={phoneDialogOpen} onOpenChange={setPhoneDialogOpen}>
        <DialogContent className="sm:max-w-md">
          <DialogHeader>
            <DialogTitle>
              {securityData.phone
                ? t("phone.dialogTitle")
                : t("phone.dialogTitleBind")}
            </DialogTitle>
            <DialogDescription>
              {t("phone.dialogDescription")}
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-4 py-4">
            <div className="space-y-2">
              <Label htmlFor="new-phone">{t("phone.newPhone")}</Label>
              <Input
                id="new-phone"
                type="tel"
                value={newPhone}
                onChange={(e) => setNewPhone(e.target.value)}
                placeholder={t("phone.newPhonePlaceholder")}
                maxLength={11}
              />
            </div>
            <div className="space-y-2">
              <Label htmlFor="verification-code">
                {t("phone.verificationCode")}
              </Label>
              <div className="flex gap-2">
                <Input
                  id="verification-code"
                  type="text"
                  value={verificationCode}
                  onChange={(e) => setVerificationCode(e.target.value)}
                  placeholder={t("phone.verificationCodePlaceholder")}
                  maxLength={6}
                  className="flex-1"
                />
                <Button
                  type="button"
                  variant="outline"
                  onClick={handleSendCode}
                  disabled={isSendingCode || countdown > 0 || !newPhone}
                  className="shrink-0"
                >
                  {isSendingCode ? (
                    <Loader2 className="h-4 w-4 animate-spin" />
                  ) : countdown > 0 ? (
                    `${countdown}s`
                  ) : (
                    t("phone.sendCode")
                  )}
                </Button>
              </div>
            </div>
          </div>
          <DialogFooter>
            <Button
              variant="outline"
              onClick={() => setPhoneDialogOpen(false)}
              disabled={isSubmitting}
            >
              {t("actions.cancel")}
            </Button>
            <Button onClick={handleUpdatePhone} disabled={isSubmitting}>
              {isSubmitting && (
                <Loader2 className="mr-2 h-4 w-4 animate-spin" />
              )}
              {t("actions.confirm")}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* 修改邮箱对话框 */}
      <Dialog open={emailDialogOpen} onOpenChange={setEmailDialogOpen}>
        <DialogContent className="sm:max-w-md">
          <DialogHeader>
            <DialogTitle>
              {securityData.email
                ? t("email.dialogTitle")
                : t("email.dialogTitleBind")}
            </DialogTitle>
            <DialogDescription>
              {t("email.dialogDescription")}
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-4 py-4">
            <div className="space-y-2">
              <Label htmlFor="new-email">{t("email.newEmail")}</Label>
              <Input
                id="new-email"
                type="email"
                value={newEmail}
                onChange={(e) => setNewEmail(e.target.value)}
                placeholder={t("email.newEmailPlaceholder")}
              />
            </div>
          </div>
          <DialogFooter>
            <Button
              variant="outline"
              onClick={() => setEmailDialogOpen(false)}
              disabled={isSubmitting}
            >
              {t("actions.cancel")}
            </Button>
            <Button onClick={handleUpdateEmail} disabled={isSubmitting}>
              {isSubmitting && (
                <Loader2 className="mr-2 h-4 w-4 animate-spin" />
              )}
              {t("email.sendVerification")}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* 注销账号确认对话框 */}
      <AlertDialog open={deleteDialogOpen} onOpenChange={setDeleteDialogOpen}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle className="text-destructive">
              {t("dangerZone.confirmTitle")}
            </AlertDialogTitle>
            <AlertDialogDescription asChild>
              <div className="space-y-4">
                <p>{t("dangerZone.confirmDescription")}</p>
                {securityData.password_set && (
                  <div className="space-y-2">
                    <Label htmlFor="delete-password">
                      {t("dangerZone.enterPassword")}
                    </Label>
                    <Input
                      id="delete-password"
                      type="password"
                      value={deletePassword}
                      onChange={(e) => setDeletePassword(e.target.value)}
                      placeholder={t("dangerZone.passwordPlaceholder")}
                    />
                  </div>
                )}
              </div>
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel disabled={isSubmitting}>
              {t("actions.cancel")}
            </AlertDialogCancel>
            <AlertDialogAction
              onClick={handleDeleteAccount}
              disabled={isSubmitting}
              className="bg-destructive text-destructive-foreground hover:bg-destructive/90"
            >
              {isSubmitting && (
                <Loader2 className="mr-2 h-4 w-4 animate-spin" />
              )}
              {t("dangerZone.confirmDelete")}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      {/* 解绑 Google 确认对话框 */}
      <AlertDialog
        open={unlinkGoogleDialogOpen}
        onOpenChange={setUnlinkGoogleDialogOpen}
      >
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>
              {t("thirdParty.unlinkGoogleTitle")}
            </AlertDialogTitle>
            <AlertDialogDescription>
              {t("thirdParty.unlinkGoogleDescription")}
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel disabled={isSubmitting}>
              {t("actions.cancel")}
            </AlertDialogCancel>
            <AlertDialogAction
              onClick={handleUnlinkGoogle}
              disabled={isSubmitting}
            >
              {isSubmitting && (
                <Loader2 className="mr-2 h-4 w-4 animate-spin" />
              )}
              {t("thirdParty.confirmUnlink")}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}
